import { useState } from "react";
import { CalendarDays } from "lucide-react";
import type { UserProfile, VisitListItem } from "@school-sales-ai/shared";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import {
  useCancelVisit,
  useMarkAttendance,
  useRescheduleVisit,
  useVisits,
  type VisitScope,
} from "@/hooks/useVisits";
import { useUsers } from "@/hooks/useUsers";
import { AttendanceConfirmDialog } from "@/components/visits/AttendanceConfirmDialog";
import { AvailabilityEditor } from "@/components/visits/AvailabilityEditor";
import { BlackoutsSection } from "@/components/visits/BlackoutsSection";
import { CancelVisitDialog } from "@/components/visits/CancelVisitDialog";
import { SlotPickerDialog } from "@/components/visits/SlotPickerDialog";
import { VisitsTable, VisitStatusBadge } from "@/components/visits/VisitsTable";
import { VisitRowActions, type VisitModalState } from "@/components/visits/VisitRowActions";
import { formatVisitDateTime } from "@/components/visits/visit-status";

type PageTab = "list" | "availability";

function PageTabs({
  tab,
  onChange,
}: {
  tab: PageTab;
  onChange: (tab: PageTab) => void;
}): React.JSX.Element {
  return (
    <div className="flex gap-1 rounded-lg bg-ink-050 p-1">
      {(
        [
          { value: "list", label: "Visitas" },
          { value: "availability", label: "Disponibilidad" },
        ] as const
      ).map((t) => (
        <button
          key={t.value}
          type="button"
          onClick={() => onChange(t.value)}
          className={cn(
            "rounded-md px-3 py-1.5 text-xs font-medium transition-colors",
            tab === t.value
              ? "bg-white font-semibold text-ink-900 shadow-sm"
              : "text-ink-500 hover:text-ink-700",
          )}
        >
          {t.label}
        </button>
      ))}
    </div>
  );
}

const SCOPE_TABS: { value: VisitScope; label: string }[] = [
  { value: "upcoming", label: "Próximas" },
  { value: "history", label: "Historial" },
];

function ScopeToggle({
  scope,
  onChange,
}: {
  scope: VisitScope;
  onChange: (scope: VisitScope) => void;
}): React.JSX.Element {
  return (
    <div className="flex gap-1 rounded-lg bg-ink-050 p-1">
      {SCOPE_TABS.map((tab) => (
        <button
          key={tab.value}
          type="button"
          onClick={() => onChange(tab.value)}
          className={cn(
            "rounded-md px-3 py-1.5 text-xs font-medium transition-colors",
            scope === tab.value
              ? "bg-white font-semibold text-ink-900 shadow-sm"
              : "text-ink-500 hover:text-ink-700",
          )}
        >
          {tab.label}
        </button>
      ))}
    </div>
  );
}

function EmptyState({ scope }: { scope: VisitScope }): React.JSX.Element {
  return (
    <div className="flex flex-col items-center justify-center gap-3 py-20">
      <div className="flex h-12 w-12 items-center justify-center rounded-full bg-ink-100">
        <CalendarDays size={22} className="text-ink-400" />
      </div>
      <p className="font-display text-lg font-bold text-ink-700">
        {scope === "upcoming" ? "Aún no hay visitas agendadas" : "Sin visitas pasadas"}
      </p>
      <p className="text-xs text-ink-400">
        {scope === "upcoming"
          ? "Cuando el agente agende una visita aparecerá aquí."
          : "Las visitas concluidas o canceladas aparecerán aquí."}
      </p>
    </div>
  );
}

function SkeletonRows(): React.JSX.Element {
  return (
    <div className="flex flex-col gap-2">
      {[0, 1, 2, 3].map((i) => (
        <div key={i} className="h-12 animate-pulse rounded-lg bg-ink-100" />
      ))}
    </div>
  );
}

function MobileVisitCard({
  visit,
  onAction,
}: {
  visit: VisitListItem;
  onAction: (state: VisitModalState) => void;
}): React.JSX.Element {
  return (
    <div className="flex items-start justify-between gap-3 rounded-lg border border-ink-100 bg-white px-3.5 py-3">
      <div className="min-w-0 flex flex-col gap-1">
        <p className="text-[13px] font-semibold text-ink-900 first-letter:uppercase">
          {formatVisitDateTime(visit.startsAt)}
        </p>
        <p className="truncate text-[12px] text-ink-600">
          {visit.leadName ?? visit.leadSessionRef}
          {visit.advisorName ? ` · Recibe: ${visit.advisorName}` : ""}
        </p>
        <div>
          <VisitStatusBadge status={visit.status} />
        </div>
      </div>
      <VisitRowActions visit={visit} onAction={onAction} />
    </div>
  );
}

export function Visits(): React.JSX.Element {
  const [pageTab, setPageTab] = useState<PageTab>("list");
  const [scope, setScope] = useState<VisitScope>("upcoming");
  const [page, setPage] = useState(1);
  const [modal, setModal] = useState<VisitModalState>(null);

  const { data, isLoading } = useVisits(scope, page);
  const { data: users = [] } = useUsers();
  const advisors = users.filter((u: UserProfile) => u.active);
  const rescheduleVisit = useRescheduleVisit();
  const cancelVisit = useCancelVisit();
  const markAttendance = useMarkAttendance();

  const items = data?.items ?? [];
  const totalPages = data ? Math.max(1, Math.ceil(data.total / data.pageSize)) : 1;

  const changeScope = (next: VisitScope): void => {
    setScope(next);
    setPage(1);
  };

  const closeModal = (): void => setModal(null);

  const content = isLoading ? (
    <SkeletonRows />
  ) : items.length === 0 ? (
    <EmptyState scope={scope} />
  ) : null;

  const pagination = totalPages > 1 && (
    <div className="flex items-center justify-end gap-2 pt-3">
      <Button
        variant="outline"
        size="sm"
        disabled={page <= 1}
        onClick={() => setPage((p) => p - 1)}
      >
        Anterior
      </Button>
      <span className="text-xs text-ink-500">
        {page} / {totalPages}
      </span>
      <Button
        variant="outline"
        size="sm"
        disabled={page >= totalPages}
        onClick={() => setPage((p) => p + 1)}
      >
        Siguiente
      </Button>
    </div>
  );

  const listContent = (mobile: boolean): React.JSX.Element => (
    <div className="flex flex-col gap-3">
      <div className="flex justify-end">
        <ScopeToggle scope={scope} onChange={changeScope} />
      </div>
      {content ?? (
        <>
          {mobile ? (
            <div className="flex flex-col gap-2">
              {items.map((visit) => (
                <MobileVisitCard key={visit.id} visit={visit} onAction={setModal} />
              ))}
            </div>
          ) : (
            <VisitsTable items={items} onAction={setModal} />
          )}
          {pagination}
        </>
      )}
    </div>
  );

  const availabilityContent = (
    <div className="flex flex-col gap-6">
      <AvailabilityEditor />
      <BlackoutsSection advisors={advisors} />
    </div>
  );

  return (
    <>
      {/* Mobile */}
      <div className="md:hidden flex flex-col flex-1 min-h-0">
        <PageHeader title="Visitas" right={<PageTabs tab={pageTab} onChange={setPageTab} />} />
        <div className="flex-1 overflow-y-auto px-4 py-4">
          {pageTab === "list" ? listContent(true) : availabilityContent}
        </div>
      </div>

      {/* Desktop */}
      <div className="hidden md:flex flex-col flex-1 min-h-0">
        <header className="flex h-18 items-center justify-between border-b border-ink-100 bg-surface px-6">
          <h1 className="font-display text-[28px] font-extrabold text-ink-900">Visitas</h1>
          <PageTabs tab={pageTab} onChange={setPageTab} />
        </header>
        <div className="flex-1 overflow-y-auto p-6">
          {pageTab === "list" ? listContent(false) : availabilityContent}
        </div>
      </div>

      {/* Action dialogs — discriminated-union modal state (Users.tsx pattern) */}
      {modal?.kind === "reschedule" && (
        <SlotPickerDialog
          open
          onOpenChange={(open) => {
            if (!open) closeModal();
          }}
          excludeBookingId={modal.visit.id}
          isSaving={rescheduleVisit.isPending}
          onSelect={(startsAt) =>
            rescheduleVisit.mutate(
              { bookingId: modal.visit.id, body: { startsAt } },
              { onSuccess: closeModal },
            )
          }
        />
      )}
      {modal?.kind === "cancel" && (
        <CancelVisitDialog
          open
          onOpenChange={(open) => {
            if (!open) closeModal();
          }}
          visitLabel={formatVisitDateTime(modal.visit.startsAt)}
          isSaving={cancelVisit.isPending}
          onConfirm={(reason) =>
            cancelVisit.mutate(
              { bookingId: modal.visit.id, body: reason ? { reason } : {} },
              { onSuccess: closeModal },
            )
          }
        />
      )}
      {modal?.kind === "attendance" && (
        <AttendanceConfirmDialog
          open
          onOpenChange={(open) => {
            if (!open) closeModal();
          }}
          status={modal.status}
          visitLabel={formatVisitDateTime(modal.visit.startsAt)}
          isSaving={markAttendance.isPending}
          onConfirm={() =>
            markAttendance.mutate(
              { bookingId: modal.visit.id, body: { status: modal.status } },
              { onSuccess: closeModal },
            )
          }
        />
      )}
    </>
  );
}
